import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Mongo } from 'meteor/mongo';
import { MethodDeclaration } from '/imports/methods';
import { ListMethodArg, ListMethodResponse, Docmnt, Dict } from '/imports/types';
import { attachReferences, deepMerge, mapById, objectValues } from '/imports/utils';
import { DataCatalog, DataCatalogProps } from '/imports/ui/DataCatalog';
import { Column } from '/imports/ui/DataTable';

export interface ListMethodCatalogProps<T extends Docmnt = any> extends Omit<DataCatalogProps<T>, 'data' | 'allIds'> {
  data?: T[];
  listMethod: MethodDeclaration<ListMethodArg<T>, ListMethodResponse<T>>;
  limit?: number;
  defaultSort?: { [k in keyof T]?: 1 | -1 };
  query?: Mongo.Query<T> | (() => Mongo.Query<T>);
  className?: string;
  onLoad?: (documents: T[]) => void;
  element?: (d: T) => React.ReactNode;
  row?: (d: T) => React.ReactNode;
  rowClass?: (d: T) => string;
  dataRef?: React.RefObject<T[]>
  onDataLoaded?: (documents: T[]) => void;
}

/**
 * DataCatalog wired to a server list method, with paging, search, sort and reference attachment.
 */
export const ListMethodCatalog = <T extends Docmnt>(props: ListMethodCatalogProps<T>) => {
  const { listMethod, columns, filters, pageSize, updateMethod, deleteMethod, tools, query,
    className, element, row, rowClass, dataRef, onDataLoaded } = props;
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [softLoading, setSoftLoading] = useState(false);
  const [data, setData] = useState<T[]>(props.data || []);
  const [allIds, setAllIds] = useState<string[]>([]);
  const [count, setCount] = useState(0);
  const [references, setReferences] = useState<Dict<Dict<Docmnt>>>({});
  const [skip, setSkip] = useState(data.length);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(props.defaultSort || {} as { [k in keyof T]?: 1 | -1 });
  const searchTimeout = useRef<NodeJS.Timeout>(undefined);
  /**
   * Calls the list method and updates data, ids, count and references.
   * @param append - append results to existing data instead of replacing
   */
  const loadData = async (append = false) => {
    try {
      setSoftLoading(true);
      const limit = props.limit || 50;
      const _search = search;
      const _query = typeof query === 'function' ? query() : query;
      const response = await listMethod.call({ limit, skip, search, sort, query: _query });
      if (_search !== search) return;
      setAllIds(response.allIds);
      setCount(response.count);
      const _references = deepMerge(references, response.$references);
      setReferences(_references);
      let _data = append ? [...data, ...response.documents] : response.documents;
      const _map = mapById(_data);
      _data = objectValues(_map);
      const _referencedData = attachReferences(_data, _references);
      setData(_referencedData);
      if (dataRef) dataRef.current = _referencedData;
      onDataLoaded?.(_referencedData);
    }
    catch (error) {
      console.log(error);
      toast.error(`${error}`);
    }
    finally {
      setLoading(false);
      setSoftLoading(false);
      setInitialized(true);
    }
  };
  useEffect(() => {
    loadData(true);
  }, []);
  useEffect(() => {
    if (!initialized) return;
    loadData(true);
  }, [skip]);
  useEffect(() => {
    if (!initialized) return;
    setSkip(0);
    loadData();
  }, [search, query, sort]);
  useEffect(() => {
    if (!loading) props.onLoad?.(data);
  }, [loading]);
  /** Advances paging to load the next page on scroll-bottom. */
  const _onScrollBottom = () => {
    setSkip(data.length);
    props.onScrollBottom?.();
  };
  /**
   * Cycles a column's sort direction through asc, desc and unsorted.
   * @param column - clicked column
   */
  const onClickColumn = (column: Column) => {
    const field = column?.field as keyof T;
    const dir = sort[field];
    delete sort[field];
    if (dir === 1) {
      setSort({ [field]: -1, ...sort });
    }
    else if (dir === -1) {
      setSort({ ...sort });
    }
    else {
      setSort({ [field]: 1, ...sort });
    }
  };
  /**
   * Debounces search input before committing the search term.
   * @param search - search text
   */
  const onSearch = (search: string) => {
    searchTimeout.current && clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setSearch(search); }, 100);
  };
  return (
    <DataCatalog
      {...props}
      data={data}
      allIds={allIds}
      references={references}
      columns={columns}
      pageSize={pageSize}
      searchFilter={() => true} // search filter is server-side
      onSearch={onSearch}
      onSearchEnter={(search) => setSearch(search || '')}
      filters={[...(filters || [])]}
      onScrollBottom={_onScrollBottom}
      updateMethod={updateMethod}
      deleteMethod={deleteMethod}
      onDelete={() => loadData()}
      count={count}
      loading={loading}
      softLoading={softLoading}
      tools={tools}
      sort={sort}
      onClickColumn={onClickColumn}
      className={className}
      element={element}
      row={row}
      rowClass={rowClass}
    />
  );
};